import React, { useContext, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import SearchBar from "../common/SearchBar";
import SearchResults from "./SearchResults";
import useWeaveSearch from "./useWeaveSearch";

import { featureCollection } from "@turf/helpers";

import Box from "@material-ui/core/Box";
import IconButton from "@material-ui/core/IconButton";
import Input from "@material-ui/core/Input";
import Fade from "@material-ui/core/Fade";

import CloseIcon from "@material-ui/icons/Close";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";

import { MapboxContext } from "../maps/MapboxContext";
import useOutsideClick from "@rooks/use-outside-click";
import { useDebounce } from "use-debounce";
import { FlyToInterpolator } from "react-map-gl";

export default React.memo(
  function SearchForm({
    open,
    onClose,
    setViewStateInBounds,
    setActiveFeatures,
    setSelectedId,
    setMarkers,
    a11y
  }) {
    /**
     * Window interactions
     */
    const popupRef = useRef();
    useOutsideClick(popupRef, () => {
      setValue("");
      onClose();
    });

    /**
     * Input interactions
     */
    const inputRef = useRef();
    useEffect(() => {
      if (open) {
        inputRef.current.focus();
      }
    }, [open]);

    /**
     * Search interactions
     */
    const [value, setValue] = useState("");
    const clearValue = () => {
      setValue("");
      inputRef.current.focus();
    };

    /**
     * Debounce search term
     */
    const [debouncedValue] = useDebounce(value, 1500);
    useEffect(() => {
      window.gtag("event", "search", {
        event_label: debouncedValue
      });
    }, [debouncedValue]);

    /**
     * API search abastracted into a hook for... reuse
     */
    const { results } = useWeaveSearch(value);

    /**
     * Handle click on a search result
     */
    const { data } = useContext(MapboxContext);

    const onResultClick = result => {
      const { y: latitude, x: longitude } = result.centroid;
      const feature = data.features.find(
        d => d.properties.permit_id === result.id
      );

      const features = feature
        ? data.features.filter(
            f => f.properties.selection_id === feature.properties.selection_id
          )
        : [];
      if (features.length) {
        /**
         * If there are features, set them as the active
         * features and also set the highlight key so
         * the resulting block goes pink
         */
        setMarkers([]);
        setSelectedId(features[0].properties.permit_id);
        setTimeout(() => setActiveFeatures(featureCollection(features)), 500);
      } else {
        /**
         * If there's no match in the dataset,
         * just show a marker.
         */
        setMarkers([{ latitude, longitude }]);
      }

      onClose();
      setViewStateInBounds(vs => ({
        ...vs,
        latitude,
        longitude,
        transitionDuration: 350,
        transitionInterpolator: new FlyToInterpolator(),
        transitionEasing: t =>
          t < 0.5 ? 8 * t * t * t * t : 1 - 8 * --t * t * t * t,
        zoom: vs.zoom < 14.5 ? 14.5 : vs.zoom
      }));

      window.gtag("event", "select_content", {
        event_label: result.display_1
      });
    };

    return (
      <Fade in={open}>
        <Box
          bgcolor="white"
          position="absolute"
          zIndex={45}
          pt={10}
          width={{ xs: "100%", md: 412 }}
          height="100%"
          boxShadow={{ xs: 0, md: 2 }}
          ref={ref => (popupRef.current = ref)}
        >
          <SearchBar>
            <IconButton onClick={onClose} aria-label="close search">
              <ChevronLeftIcon />
            </IconButton>
            <SearchInput
              value={value}
              placeholder="Search"
              label="Find a building"
              disableUnderline={true}
              inputRef={ref => (inputRef.current = ref)}
              onChange={event => setValue(event.target.value)}
              style={{ fontSize: 20 }}
            />
            {value !== "" && (
              <IconButton onClick={clearValue} aria-label="reset search">
                <CloseIcon />
              </IconButton>
            )}
          </SearchBar>
          <SearchResults results={results} onResultClick={onResultClick} />
        </Box>
      </Fade>
    );
  },
  ({ open: prev }, { open: next }) => prev === next
);

const SearchInput = styled(Input)`
  flex-grow: 1;
`;
